import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Brain } from "lucide-react";
import { cn } from "@/lib/utils";
import { openBrainChat } from "@/components/brain/ChatContainer";
import { toggleBrainChatForeground } from "@/lib/brainChatForeground";
import { toggleBrainFreeInput } from "@/components/brain/BrainFreeInput";


const STORAGE_KEY = "central-brain-history";

function hasBrainMessages(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

interface CentralBrainCoreProps {
  alertPriority?: number;
}

export default function CentralBrainCore({ alertPriority = 5 }: CentralBrainCoreProps) {
  const isUrgent = alertPriority <= 2;
  const hasAlert = alertPriority <= 3;

  const [alertSeen, setAlertSeen] = useState(false);

  // Reset alertSeen when alert status changes
  useEffect(() => {
    if (hasAlert) {
      setAlertSeen(false);
    }
  }, [hasAlert]);

  const handleBrainClick = () => {
    const messagesExist = hasBrainMessages();

    if (messagesExist) {
      // Brain has chat history → open the real chat
      if (hasAlert) setAlertSeen(true);
      toggleBrainChatForeground();
      openBrainChat();
    } else {
      // No messages → toggle inline free input zone
      toggleBrainFreeInput();
    }
  };

  // Brain icon color
  const brainColor = isUrgent
    ? "text-destructive"
    : hasAlert
      ? "text-amber-500"
      : "text-primary";

  return (
    <div className="relative flex flex-col items-center">
      <button
        onClick={handleBrainClick}
        className="relative group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
        aria-label="Ouvrir le Cerveau Central"
        title="Cerveau Central"
      >
        {/* Pulsing glow ring — ONLY when brain has something to say and not yet seen */}
        {hasAlert && !alertSeen && (
          <motion.div
            className={cn(
              "absolute inset-0 rounded-full",
              isUrgent
                ? "bg-destructive/20 shadow-[0_0_24px_hsl(0,72%,51%,0.3)]"
                : "bg-amber-500/15 shadow-[0_0_20px_hsl(38,92%,50%,0.2)]"
            )}
            animate={{
              scale: [1, 1.25, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: isUrgent ? 1.5 : 2.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}

        <div
          className={cn(
            "relative z-10 p-1.5 rounded-full transition-all duration-300",
            "group-hover:scale-110 group-active:scale-95",
            hasAlert
              ? isUrgent
                ? "border-2 border-destructive/50"
                : "border-2 border-amber-500/40"
              : ""
          )}
        >
          <Brain className={cn("h-7 w-7 transition-colors duration-300", brainColor)} />
        </div>

        {/* Red dot for urgent — disappears once seen */}
        {isUrgent && !alertSeen && (
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-destructive animate-pulse ring-2 ring-background z-20" />
        )}
      </button>
    </div>
  );
}
